import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Heading,
  AspectRatio,
  Flex,
  Text,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaPlay } from 'react-icons/fa';
import {
  CIL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
  YOUTUBE_VIDEOS,
} from './constants';

const EMBED_BASE = 'https://www.youtube.com/embed';
const THUMB_BASE = 'https://img.youtube.com/vi';

const thumbUrl = (id) => `${THUMB_BASE}/${id}/mqdefault.jpg`;

const YouTubeMasonrySection = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const selected = YOUTUBE_VIDEOS[selectedIndex];
  const isShort = selected?.short ?? false;

  const handleSelect = useCallback((index) => {
    setSelectedIndex(index);
    setIsPlaying(true);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const playerWrapRef = useRef(null);
  const [playerHeight, setPlayerHeight] = useState(null);
  const isDesktop = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    if (!isDesktop || !playerWrapRef.current) return undefined;
    const el = playerWrapRef.current;
    const observer = new ResizeObserver(([entry]) => {
      setPlayerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    setPlayerHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isDesktop, selected?.id, isPlaying]);

  const listMaxH = useBreakpointValue({
    base: '20rem',
    md: playerHeight != null ? `${playerHeight}px` : '33rem',
  });

  return (
    <Box
      as="section"
      id="section-video"
      py={{ base: 10, md: 14 }}
      scrollMarginTop={{ base: '80px', md: '140px' }}
    >
      <Box w="100%" maxW="100%" px={{ base: 4, md: 6, lg: 8 }}>
        <Heading as="h2" size="lg" mb={2} textAlign="center">
          <Box
            w="100%"
            px={4}
            py={3}
            borderRadius="lg"
            sx={{ background: SECTION_TITLE_CONTAINER_BG }}
            boxShadow="sm"
          >
            <Text as="span" fontWeight="bold" color={SECTION_TITLE_TEXT_COLOR}>
              Video
            </Text>
          </Box>
        </Heading>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={6}
          align={{ md: 'center' }}
          mt={6}
        >
          {/* Player unico */}
          <Box ref={playerWrapRef} flex="1" minW={0} w="100%">
            <AspectRatio
              ratio={isShort ? 9 / 16 : 16 / 9}
              maxW={isShort ? { base: '320px', sm: '280px' } : undefined}
              mx={isShort ? 'auto' : undefined}
            >
              <Box position="relative" w="100%" h="100%" bg="black">
                {!isPlaying ? (
                  <>
                    <Box
                      as="img"
                      src={thumbUrl(selected.id)}
                      alt=""
                      position="absolute"
                      inset={0}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <Flex
                      position="absolute"
                      inset={0}
                      align="center"
                      justify="center"
                      bg="blackAlpha.400"
                    >
                      <IconButton
                        aria-label="Riproduci video"
                        icon={<FaPlay />}
                        size="lg"
                        w={{ base: 14, md: 16 }}
                        h={{ base: 14, md: 16 }}
                        borderRadius="full"
                        colorScheme="yellow"
                        color={CIL.plum}
                        bg={CIL.yellow}
                        _hover={{
                          bg: CIL.yellowLight,
                          transform: 'scale(1.05)',
                        }}
                        onClick={handlePlay}
                      />
                    </Flex>
                  </>
                ) : (
                  <Box
                    as="iframe"
                    position="absolute"
                    inset={0}
                    w="100%"
                    h="100%"
                    src={`${EMBED_BASE}/${selected.id}?rel=0&autoplay=1`}
                    title={selected.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </Box>
            </AspectRatio>
          </Box>

          {/* Elenco video con titoli – scroll verticale */}
          <Flex
            direction="column"
            gap={3}
            overflowX="hidden"
            overflowY="auto"
            maxH={listMaxH}
            minW={{ base: '100%', md: '200px' }}
            maxW={{ md: '240px' }}
            bg="white"
            boxShadow="sm"
            borderRadius="lg"
            p={3}
            borderWidth="1px"
            borderColor="gray.100"
            sx={{
              '&::-webkit-scrollbar': { w: 1 },
              '&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: 2 },
            }}
          >
            {YOUTUBE_VIDEOS.map((video, index) => (
              <Flex
                key={video.id}
                as="button"
                type="button"
                onClick={() => handleSelect(index)}
                align="center"
                gap={3}
                p={2}
                borderRadius="md"
                bg={selectedIndex === index ? 'gray.50' : 'transparent'}
                _hover={{ bg: 'gray.50' }}
                textAlign="left"
                w="100%"
                flexShrink={0}
              >
                <Box
                  position="relative"
                  flexShrink={0}
                  h="72px"
                  w="128px"
                  borderRadius="xl"
                  overflow="hidden"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.100"
                >
                  <Box
                    as="img"
                    src={thumbUrl(video.id)}
                    alt=""
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    bg="blackAlpha.300"
                  >
                    <Box color="white" fontSize="lg">
                      <FaPlay />
                    </Box>
                  </Flex>
                </Box>
                <Text
                  fontSize="sm"
                  fontWeight={selectedIndex === index ? 600 : 500}
                  color="gray.800"
                  noOfLines={2}
                >
                  {video.title}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default YouTubeMasonrySection;
